import type { Candle } from '@/components/charts/PriceChart'
import { sessionsIn } from '@/features/market/api/barWindow'
import type { ChartBar } from '@/schemas/trading-chart'

/*
 * The Signal Zone candle chart (the dashboard's NEPSE, the Signal page's
 * stock): which bars it draws and which overlays it asks for. No components
 * here, so a route's loader can start the reads with the same keys the chart
 * uses.
 *
 * The bars are the TradingView chart's own first read (the newest 1,000
 * sessions, `chartHistoryHeadQueryOptions`): the chart's first `getBars`, the
 * session's price line and the fallback candles all answer from it, so the
 * section makes one history read. A thousand covers the window behind the
 * oldest session the picker offers many times over.
 */

export const NEPSE_SYMBOL = 'NEPSE'

/** About three months of sessions, the span the design's chart draws (June to September). */
export const NEPSE_WINDOW = sessionsIn(91)

/**
 * How far back the overlays reach: about a year of sessions, mm-be's own
 * default. The chart can be dragged back past the three months it opens on,
 * and its marks should still be there.
 */
export const NEPSE_OVERLAY_SESSIONS = 250

/** A bar's trading day, `YYYY-MM-DD`: mm-be stamps each at 00:00 UTC of its day. */
export const barDate = (bar: ChartBar): string => new Date(bar.time).toISOString().slice(0, 10)

/** A session (`YYYY-MM-DD`) as the epoch seconds its bar is stamped with, which the chart's points take. */
export const sessionTime = (date: string): number => Date.parse(`${date}T00:00:00Z`) / 1000

/** The session's close against the close before it, in points and percent. */
export interface NepseMove {
  change: number
  /** Null when the close before it was zero, which no percent can be measured from. */
  percent: number | null
}

export interface NepseWindow {
  /**
   * The bars up to and including the session, oldest first, for the fallback
   * candles (times in seconds). No volume: the frame draws prices alone (f01).
   */
  candles: Candle[]
  /** The session's own bar, or null when the bars hold no row for that day. */
  session: ChartBar | null
  /** The session bar's move on the bar before it; null without both. */
  move: NepseMove | null
}

/**
 * The window that ENDS on the dashboard's session (the Signal page: its
 * stock's latest bar), so the last candle, the session's open/high/low/close
 * line and the counts above all describe the same day. ISO dates compare as
 * strings, so "up to" needs no date parsing.
 */
export const nepseWindow = (bars: readonly ChartBar[], date: string): NepseWindow => {
  const shown = bars.filter((bar) => barDate(bar) <= date).slice(-NEPSE_WINDOW)
  const last = shown.at(-1)
  const before = shown.at(-2)
  const session = last !== undefined && barDate(last) === date ? last : null
  return {
    candles: shown.map(({ time, open, high, low, close }) => ({
      time: time / 1000,
      open,
      high,
      low,
      close,
    })),
    session,
    move:
      session === null || before === undefined
        ? null
        : {
            change: session.close - before.close,
            percent:
              before.close === 0 ? null : ((session.close - before.close) / before.close) * 100,
          },
  }
}
